import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AdminProjectImages } from './AdminProjectImages';
import { LayoutSelector } from './LayoutSelector';
import { CaretRight } from '@phosphor-icons/react';

interface Project {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sort_order: number;
  published: boolean;
  layout_type: string;
  section_slug: string;
  transition_type: string | null;
  transition_duration: number | null;
  transition_easing: string | null;
}

const PAGE_TRANSITIONS = [
  { value: '', label: 'Padrão Global' },
  { value: 'none', label: 'Nenhuma' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide-left', label: 'Slide Left' },
  { value: 'slide-right', label: 'Slide Right' },
  { value: 'slide-up', label: 'Slide Up' },
  { value: 'slide-down', label: 'Slide Down' },
  { value: 'scale', label: 'Scale' },
];

const EASING_OPTIONS = [
  { value: 'ease', label: 'Ease' },
  { value: 'ease-in', label: 'Ease In' },
  { value: 'ease-out', label: 'Ease Out' },
  { value: 'ease-in-out', label: 'Ease In-Out' },
];

const monoFont = "'IBM Plex Mono', monospace";
const C = {
  fg: 'rgba(247,246,242,0.85)',
  fgMuted: 'rgba(247,246,242,0.45)',
  border: 'rgba(247,246,242,0.15)',
  borderStrong: 'rgba(247,246,242,0.25)',
};

function normalizeLayoutType(layout: string | null | undefined): string {
  if (!layout) return 'A';
  if (layout === 'split-vertical') return 'A';
  if (layout === 'vertical-scroll') return 'B';
  if (layout === 'horizontal-scroll') return 'C';
  if (layout === 'split-horizontal') return 'D';
  return layout;
}

interface AdminProjectEditProps {
  projectId: string;
  onBack: () => void;
}

export function AdminProjectEdit({ projectId, onBack }: AdminProjectEditProps) {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingLayout, setPendingLayout] = useState<string | null>(null);

  const fetchProject = async () => {
    const { data } = await supabase.from('projects').select('*').eq('id', projectId).single();
    if (data) setProject(data as Project);
    setLoading(false);
    setPendingLayout(null);
  };

  useEffect(() => { fetchProject(); }, [projectId]);

  const applyLayout = async () => {
    if (!pendingLayout || !project) return;
    await supabase.from('projects').update({ layout_type: pendingLayout }).eq('id', project.id);
    setPendingLayout(null);
    fetchProject();
  };

  if (loading || !project) {
    return <p style={{ fontFamily: monoFont, color: C.fgMuted }}>Carregando...</p>;
  }

  const savedLayout = normalizeLayoutType(project.layout_type);
  const currentLayout = pendingLayout || savedLayout;
  const hasLayoutChange = Boolean(pendingLayout && pendingLayout !== savedLayout);

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 mb-6"
        style={{
          fontFamily: monoFont, fontSize: 12, fontWeight: 300,
          color: C.fgMuted, background: 'none', border: 'none', cursor: 'pointer',
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = C.fg; }}
        onMouseLeave={e => { e.currentTarget.style.color = C.fgMuted; }}
      >
        ← VOLTAR
      </button>

      {/* Project name */}
      <h2
        className="uppercase mb-6"
        style={{ fontFamily: monoFont, fontSize: 16, fontWeight: 300, color: '#F7F6F2' }}
      >
        {project.name}
      </h2>

      {/* Layout selector */}
      <LayoutSelector
        currentLayout={currentLayout}
        onSelect={(layout) => setPendingLayout(layout)}
        hasChange={hasLayoutChange}
        onApply={applyLayout}
      />

      {/* Transition override */}
      <ProjectTransitionOverride project={project} onUpdate={fetchProject} />

      {/* Images */}
      <div style={{ marginTop: 16 }}>
        <AdminProjectImages projectId={project.id} sectionSlug={project.section_slug} />
      </div>
    </div>
  );
}

function ProjectTransitionOverride({ project, onUpdate }: { project: Project; onUpdate: () => void }) {
  const [open, setOpen] = useState(false);
  const isCustom = Boolean(project.transition_type);

  const updateTransition = async (field: string, value: string | number | null) => {
    await supabase.from('projects').update({ [field]: value || null } as any).eq('id', project.id);
    onUpdate();
  };

  const clearOverride = async () => {
    await supabase.from('projects').update({ transition_type: null, transition_duration: null, transition_easing: null } as any).eq('id', project.id);
    onUpdate();
  };

  const pillStyle = (active: boolean): React.CSSProperties => ({
    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '4px 10px', cursor: 'pointer',
    border: active ? '1px solid #F7F6F2' : `1px solid ${C.border}`,
    backgroundColor: active ? '#F7F6F2' : 'transparent',
    color: active ? '#000000' : C.fgMuted,
    transition: 'all 0.15s ease',
  });

  return (
    <div style={{ borderTop: '0.5px solid rgba(247,246,242,0.08)', paddingTop: 8, marginBottom: 8 }}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2" style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer', color: C.fgMuted }}>
        <CaretRight size={10} weight="thin" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
        <span>Transição de entrada</span>
        <span style={{ fontSize: 12, padding: '2px 8px', border: `1px solid ${C.border}`, color: isCustom ? 'rgba(247,246,242,0.8)' : C.fgMuted }}>
          {isCustom ? 'Customizada' : 'Padrão global'}
        </span>
      </button>
      {open && (
        <div className="mt-3 ml-4 flex flex-col gap-3">
          <div className="flex gap-1 flex-wrap">
            {PAGE_TRANSITIONS.map(opt => (
              <button key={opt.value} onClick={() => updateTransition('transition_type', opt.value || null)} style={pillStyle((project.transition_type || '') === opt.value)}>
                {opt.label}
              </button>
            ))}
          </div>
          {isCustom && project.transition_type !== 'none' && (
            <>
              <div className="flex flex-col gap-1">
                <label style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', color: C.fgMuted }}>
                  Duração: {project.transition_duration || 600}ms
                </label>
                <input type="range" min={200} max={1200} step={50} value={project.transition_duration || 600}
                  onChange={(e) => updateTransition('transition_duration', parseInt(e.target.value))}
                  style={{ width: 200, accentColor: '#F7F6F2' }}
                />
              </div>
              <div className="flex gap-1 flex-wrap">
                {EASING_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => updateTransition('transition_easing', opt.value)} style={pillStyle((project.transition_easing || 'ease') === opt.value)}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
          {isCustom && (
            <button onClick={clearOverride} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', color: C.fgMuted, background: 'none', border: 'none', cursor: 'pointer', alignSelf: 'flex-start', transition: 'color 0.15s ease' }}
              onMouseEnter={e => { e.currentTarget.style.color = C.fg; }}
              onMouseLeave={e => { e.currentTarget.style.color = C.fgMuted; }}
            >
              ✕ Usar padrão global
            </button>
          )}
        </div>
      )}
    </div>
  );
}
